import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, fmtAmount as fmt, formatPercent, unwrap } from "../api";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  Stat,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useResource } from "../useResource";

/** Negative margins are the ones worth spotting from across the room. */
function toneOf(amount: string) {
  return amount.startsWith("-") ? "text-red-600" : "";
}

const BASES = [
  ["direct_cost_share", "Share of direct cost"],
  ["revenue_share", "Share of revenue"],
  ["equal", "Split equally"],
  ["manual_weight", "Manual weights"],
] as const;

function thisMonth() {
  return `${new Date().toISOString().slice(0, 7)}-01`;
}

export function Reports({ slug, currency }: { slug: string; currency: string }) {
  const qc = useQueryClient();
  const [showRule, setShowRule] = useState(false);
  const [period, setPeriod] = useState(thisMonth());
  const [runError, setRunError] = useState<string | null>(null);
  const [runNote, setRunNote] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ["reports-overview", slug],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].reports.overview.$get({ param: { slug }, query: {} })),
  });
  const pnlTable = useTable(overview.data?.projects ?? [], {
    filter: (r, q) =>
      `${r.code} ${r.name} ${r.clientName ?? ""}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      project: (r) => (r.name ?? "").toLowerCase(),
      client: (r) => (r.clientName ?? "").toLowerCase(),
      invoiced: (r) => Number(r.invoiced),
      directCost: (r) => Number(r.directCost),
      gross: (r) => Number(r.grossMargin),
      overhead: (r) => Number(r.allocatedCost),
      net: (r) => Number(r.netMargin),
    },
  });

  const cash = useQuery({
    queryKey: ["reports-cash", slug],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].reports["cash-by-month"].$get({ param: { slug }, query: {} }),
      ),
  });
  const cashTable = useTable(cash.data?.months ?? [], {
    filter: (m, q) => m.period.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      month: (m) => m.period,
      in: (m) => Number(m.cashIn),
      out: (m) => Number(m.cashOut),
      net: (m) => Number(m.net),
    },
  });

  const projects = useQuery({
    queryKey: ["projects", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"].projects.$get({ param: { slug } })),
  });
  const costCodes = useQuery({
    queryKey: ["cost-codes", slug],
    queryFn: async () => unwrap(await api.api.c[":slug"]["cost-codes"].$get({ param: { slug } })),
  });
  const allocated = useQuery({
    queryKey: ["allocations", slug, period],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].allocations.$get({ param: { slug }, query: { period } })),
  });
  const allocTable = useTable(allocated.data?.allocations ?? [], {
    filter: (a, q) =>
      `${a.projectName} ${a.expenseDescription} ${a.ruleName ?? ""}`
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      project: (a) => a.projectName.toLowerCase(),
      from: (a) => a.expenseDescription.toLowerCase(),
      rule: (a) => (a.ruleName ?? "").toLowerCase(),
      weight: (a) => Number(a.weight),
      amount: (a) => Number(a.amount),
    },
  });

  const rules = useResource({
    key: ["allocation-rules", slug],
    list: async () => unwrap(await api.api.c[":slug"].allocations.rules.$get({ param: { slug } })),
    create: async (body: Record<string, unknown>) =>
      unwrap(
        await api.api.c[":slug"].allocations.rules.$post({ param: { slug }, json: body as never }),
      ),
  });
  const rulesTable = useTable(rules.query.data?.rules ?? [], {
    filter: (r, q) =>
      `${r.name} ${BASES.find(([v]) => v === r.basis)?.[1] ?? r.basis} ${r.costCode ?? ""} ${r.costCodeName ?? ""} ${r.weights.map((w) => w.projectName).join(" ")}`
        .toLowerCase()
        .includes(q.toLowerCase()),
    sorters: {
      rule: (r) => r.name.toLowerCase(),
      basis: (r) => (BASES.find(([v]) => v === r.basis)?.[1] ?? r.basis).toLowerCase(),
      catches: (r) =>
        (r.costCode ? `${r.costCode} ${r.costCodeName}` : "Every company expense").toLowerCase(),
      weights: (r) =>
        r.weights
          .map((w) => `${w.projectName} ${w.weight}`)
          .join(", ")
          .toLowerCase(),
    },
  });

  const run = useMutation({
    mutationFn: async () =>
      unwrap(await api.api.c[":slug"].allocations.run.$post({ param: { slug }, json: { period } })),
    onMutate: () => {
      setRunError(null);
      setRunNote(null);
    },
    onSuccess: async (data) => {
      const r = data.run;
      setRunNote(
        r.written === 0 && r.replaced === 0
          ? `Nothing changed — ${r.expenses} company expense${r.expenses === 1 ? "" : "s"} already allocated for this period.`
          : `${r.written} allocation${r.written === 1 ? "" : "s"} written${r.replaced ? `, ${r.replaced} replaced` : ""} across ${r.expenses} expense${r.expenses === 1 ? "" : "s"}.`,
      );
      for (const k of ["allocations", "reports-overview"]) {
        await qc.invalidateQueries({ queryKey: [k, slug] });
      }
    },
    onError: (e) => setRunError(e instanceof ApiError ? e.message : "Couldn't run the allocation."),
  });

  if (overview.isPending) return <Loading />;
  if (overview.isError) return <ErrorNote>{(overview.error as Error).message}</ErrorNote>;

  const t = overview.data.totals;
  const rows = overview.data.projects;

  return (
    <>
      <Panel title="Where the business stands">
        <div className="mb-6 grid gap-3 sm:grid-cols-5">
          <Stat label="Invoiced" value={fmt(t.invoiced)} currency={currency} />
          <Stat label="Direct cost" value={fmt(t.directCost)} currency={currency} />
          <Stat label="Gross margin" value={fmt(t.grossMargin)} currency={currency} />
          <Stat
            label="Net margin"
            value={fmt(t.netMargin)}
            currency={currency}
            hint="After overhead"
          />
          <Stat
            label="Profit"
            value={fmt(t.profit)}
            currency={currency}
            hint="Full contract value, regardless of what's billed"
            emphasis
          />
        </div>

        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          Gross margin is what a project manager controls — revenue against cost booked straight to
          the job. Net margin adds that job's share of company overhead, which is the number the
          business actually made. They're separate accounts on the ledger precisely so nobody is
          judged on rent. Profit is a different question: it counts every job's full contract value
          and full committed cost, whether or not either side has actually been invoiced or billed
          yet — what the work is worth outright, not what's landed on the books so far.
        </p>

        {rows.length === 0 ? (
          <Empty title="No project activity yet">
            Invoice something, or record a cost against a project.
          </Empty>
        ) : (
          <>
            <TableSearch
              value={pnlTable.query}
              onChange={pnlTable.setQuery}
              placeholder="Filter projects…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="project"
                    active={pnlTable.sortKey === "project"}
                    dir={pnlTable.dir}
                    onSort={pnlTable.toggleSort}
                  >
                    Project
                  </Th>
                  <Th
                    sortKey="client"
                    active={pnlTable.sortKey === "client"}
                    dir={pnlTable.dir}
                    onSort={pnlTable.toggleSort}
                  >
                    Client
                  </Th>
                  <Th
                    right
                    sortKey="invoiced"
                    active={pnlTable.sortKey === "invoiced"}
                    dir={pnlTable.dir}
                    onSort={pnlTable.toggleSort}
                  >
                    Invoiced
                  </Th>
                  <Th
                    right
                    sortKey="directCost"
                    active={pnlTable.sortKey === "directCost"}
                    dir={pnlTable.dir}
                    onSort={pnlTable.toggleSort}
                  >
                    Direct cost
                  </Th>
                  <Th
                    right
                    sortKey="gross"
                    active={pnlTable.sortKey === "gross"}
                    dir={pnlTable.dir}
                    onSort={pnlTable.toggleSort}
                  >
                    Gross
                  </Th>
                  <Th
                    right
                    sortKey="overhead"
                    active={pnlTable.sortKey === "overhead"}
                    dir={pnlTable.dir}
                    onSort={pnlTable.toggleSort}
                  >
                    Overhead
                  </Th>
                  <Th
                    right
                    sortKey="net"
                    active={pnlTable.sortKey === "net"}
                    dir={pnlTable.dir}
                    onSort={pnlTable.toggleSort}
                  >
                    Net
                  </Th>
                </>
              }
            >
              {pnlTable.rows.map((r) => (
                <tr
                  key={r.projectId ?? "none"}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>
                    <span className="tabular text-neutral-500">{r.code}</span> {r.name}
                  </Td>
                  <Td muted>{r.clientName ?? "—"}</Td>
                  <Td right mono>
                    {fmt(r.invoiced)}
                  </Td>
                  <Td right mono muted>
                    {fmt(r.directCost)}
                  </Td>
                  <Td right mono>
                    <span className={toneOf(r.grossMargin)}>{fmt(r.grossMargin)}</span>
                  </Td>
                  <Td right mono muted>
                    {fmt(r.allocatedCost)}
                  </Td>
                  <Td right mono>
                    <span className={`font-medium ${toneOf(r.netMargin)}`}>{fmt(r.netMargin)}</span>
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        )}

        {overview.data.company.unallocated !== "0.00" && (
          <p className="mt-3 text-sm text-neutral-500">
            <span className="tabular text-neutral-900">
              {fmt(overview.data.company.unallocated)} {currency}
            </span>{" "}
            of company cost has not been spread onto any project. Run the close below.
          </p>
        )}
      </Panel>

      <Panel title="Cash by month">
        {cash.isPending ? (
          <Loading />
        ) : !cash.data?.months.length ? (
          <Empty title="No cash movement yet" />
        ) : (
          <>
            <TableSearch
              value={cashTable.query}
              onChange={cashTable.setQuery}
              placeholder="Filter months…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="month"
                    active={cashTable.sortKey === "month"}
                    dir={cashTable.dir}
                    onSort={cashTable.toggleSort}
                  >
                    Month
                  </Th>
                  <Th
                    right
                    sortKey="in"
                    active={cashTable.sortKey === "in"}
                    dir={cashTable.dir}
                    onSort={cashTable.toggleSort}
                  >
                    In
                  </Th>
                  <Th
                    right
                    sortKey="out"
                    active={cashTable.sortKey === "out"}
                    dir={cashTable.dir}
                    onSort={cashTable.toggleSort}
                  >
                    Out
                  </Th>
                  <Th
                    right
                    sortKey="net"
                    active={cashTable.sortKey === "net"}
                    dir={cashTable.dir}
                    onSort={cashTable.toggleSort}
                  >
                    Net
                  </Th>
                </>
              }
            >
              {cashTable.rows.map((m) => (
                <tr
                  key={m.period}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td mono>{m.period.slice(0, 7)}</Td>
                  <Td right mono muted>
                    {fmt(m.cashIn)}
                  </Td>
                  <Td right mono muted>
                    {fmt(m.cashOut)}
                  </Td>
                  <Td right mono>
                    <span className={toneOf(m.net)}>{fmt(m.net)}</span>
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Panel>

      <Panel
        title="Overhead allocation"
        action={
          <Button
            onClick={() => {
              setShowRule(!showRule);
              rules.clearErrors();
            }}
          >
            {showRule ? "Cancel" : "New rule"}
          </Button>
        }
      >
        <p className="mb-3 max-w-prose text-sm text-neutral-500">
          A rule decides how company-scope expenses get spread across projects. The monthly cron
          runs the closed month automatically; the button below re-runs any period by hand. A re-run
          that changes nothing writes nothing.
        </p>

        {showRule && (
          <RuleForm
            costCodes={costCodes.data?.costCodes ?? []}
            projects={projects.data?.projects ?? []}
            errors={rules.fieldErrors}
            formError={rules.formError}
            saving={rules.saving}
            onSubmit={async (body) => {
              await rules.create(body);
              setShowRule(false);
            }}
          />
        )}

        {rules.query.data && rules.query.data.rules.length > 0 ? (
          <>
            <TableSearch
              value={rulesTable.query}
              onChange={rulesTable.setQuery}
              placeholder="Filter rules…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="rule"
                    active={rulesTable.sortKey === "rule"}
                    dir={rulesTable.dir}
                    onSort={rulesTable.toggleSort}
                  >
                    Rule
                  </Th>
                  <Th
                    sortKey="basis"
                    active={rulesTable.sortKey === "basis"}
                    dir={rulesTable.dir}
                    onSort={rulesTable.toggleSort}
                  >
                    Basis
                  </Th>
                  <Th
                    sortKey="catches"
                    active={rulesTable.sortKey === "catches"}
                    dir={rulesTable.dir}
                    onSort={rulesTable.toggleSort}
                  >
                    Catches
                  </Th>
                  <Th
                    sortKey="weights"
                    active={rulesTable.sortKey === "weights"}
                    dir={rulesTable.dir}
                    onSort={rulesTable.toggleSort}
                  >
                    Weights
                  </Th>
                </>
              }
            >
              {rulesTable.rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>
                    {r.name}
                    {!r.isActive && <span className="ml-2 text-xs text-neutral-500">paused</span>}
                  </Td>
                  <Td muted>{BASES.find(([v]) => v === r.basis)?.[1] ?? r.basis}</Td>
                  <Td muted>
                    {r.costCode ? `${r.costCode} ${r.costCodeName}` : "Every company expense"}
                  </Td>
                  <Td muted>
                    {r.weights.length > 0 &&
                      r.weights.map((w) => `${w.projectName} ${w.weight}`).join(", ")}
                  </Td>
                </tr>
              ))}
            </Table>
          </>
        ) : (
          <Empty title="No allocation rules yet">
            Without one, company costs stay company costs and net margin equals gross.
          </Empty>
        )}

        <div className="mt-6 flex flex-wrap items-end gap-3">
          <Field label="Period">
            <input
              className={`${input} w-44`}
              type="month"
              value={period.slice(0, 7)}
              onChange={(e) => setPeriod(`${e.target.value}-01`)}
            />
          </Field>
          <Button
            disabled={run.isPending || !rules.query.data?.rules.length}
            onClick={() => run.mutate()}
          >
            {run.isPending ? "Running…" : "Run this period"}
          </Button>
        </div>

        {runError && <ErrorNote>{runError}</ErrorNote>}
        {runNote && <p className="mt-3 text-sm text-neutral-600">{runNote}</p>}

        {allocated.data && allocated.data.allocations.length > 0 && (
          <div className="mt-6">
            <TableSearch
              value={allocTable.query}
              onChange={allocTable.setQuery}
              placeholder="Filter allocations…"
            />
            <Table
              head={
                <>
                  <Th
                    sortKey="project"
                    active={allocTable.sortKey === "project"}
                    dir={allocTable.dir}
                    onSort={allocTable.toggleSort}
                  >
                    Project
                  </Th>
                  <Th
                    sortKey="from"
                    active={allocTable.sortKey === "from"}
                    dir={allocTable.dir}
                    onSort={allocTable.toggleSort}
                  >
                    From
                  </Th>
                  <Th
                    sortKey="rule"
                    active={allocTable.sortKey === "rule"}
                    dir={allocTable.dir}
                    onSort={allocTable.toggleSort}
                  >
                    Rule
                  </Th>
                  <Th
                    right
                    sortKey="weight"
                    active={allocTable.sortKey === "weight"}
                    dir={allocTable.dir}
                    onSort={allocTable.toggleSort}
                  >
                    Weight
                  </Th>
                  <Th
                    right
                    sortKey="amount"
                    active={allocTable.sortKey === "amount"}
                    dir={allocTable.dir}
                    onSort={allocTable.toggleSort}
                  >
                    Amount
                  </Th>
                </>
              }
            >
              {allocTable.rows.map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                >
                  <Td>{a.projectName}</Td>
                  <Td muted>{a.expenseDescription}</Td>
                  <Td muted>{a.ruleName ?? "—"}</Td>
                  <Td right mono muted>
                    {formatPercent(a.weight)}
                  </Td>
                  <Td right mono>
                    {fmt(a.amount)}
                  </Td>
                </tr>
              ))}
            </Table>
            <p className="mt-3 text-sm text-neutral-500">
              Allocated this period{" "}
              <span className="tabular text-neutral-900">
                {fmt(allocated.data.totals?.total ?? "0.00")} {currency}
              </span>
            </p>
          </div>
        )}
      </Panel>
    </>
  );
}

function RuleForm({
  costCodes,
  projects,
  errors,
  formError,
  saving,
  onSubmit,
}: {
  costCodes: { id: string; code: string; name: string }[];
  projects: { id: string; name: string }[];
  errors: Record<string, string>;
  formError: string | null;
  saving: boolean;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [basis, setBasis] = useState<string>("direct_cost_share");
  const [costCodeId, setCostCodeId] = useState("");
  const [weights, setWeights] = useState<Record<string, string>>({});

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await onSubmit({
        name,
        basis,
        costCodeId: costCodeId || null,
        // Only sent for manual_weight; the other bases derive their own from
        // the period's activity, and a stale weight table would mislead.
        ...(basis === "manual_weight"
          ? {
              weights: Object.entries(weights)
                .filter(([, w]) => w !== "" && w !== "0")
                .map(([projectId, weight]) => ({ projectId, weight })),
            }
          : {}),
      });
      setName("");
    } catch {
      // Surfaced through formError / errors.
    }
  }

  return (
    <form
      onSubmit={submit}
      className="mb-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
    >
      {formError && <ErrorNote>{formError}</ErrorNote>}

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Name" error={errors.name}>
          <input
            className={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Office overhead"
            required
          />
        </Field>

        <Field label="Spread by" error={errors.basis}>
          <select className={input} value={basis} onChange={(e) => setBasis(e.target.value)}>
            {BASES.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Applies to"
          hint="Blank catches every company expense"
          error={errors.costCodeId}
        >
          <select
            className={input}
            value={costCodeId}
            onChange={(e) => setCostCodeId(e.target.value)}
          >
            <option value="">Every company expense</option>
            {costCodes.map((cc) => (
              <option key={cc.id} value={cc.id}>
                {cc.code} — {cc.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {basis === "manual_weight" && (
        <div className="mt-4">
          <p className="mb-2 max-w-prose text-sm text-neutral-500">
            Relative weights — 2 takes twice the share of 1. They don't need to add to anything; the
            split is proportional and the last project absorbs the rounding.
          </p>
          {errors.weights && <ErrorNote>{errors.weights}</ErrorNote>}
          <div className="flex flex-col gap-2">
            {projects.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                <span className="w-64 truncate text-sm">{p.name}</span>
                <input
                  className={`${input} w-28`}
                  inputMode="decimal"
                  placeholder="0"
                  value={weights[p.id] ?? ""}
                  onChange={(e) => setWeights({ ...weights, [p.id]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3">
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : "Create rule"}
        </Button>
      </div>
    </form>
  );
}
